import { RSAA } from "redux-api-middleware";
import { BookingDialogStep, Direction, IBookingTariffSegment, IBundle, IRoute, IRouteLeg, ISeat, IStop, ITravelDetailsState } from "./Models";
import { getDefaultHeaders, getSettings } from "./Settings";

export type BookingDialogActionType = "showDialog" | "setStep" |
"selectRoute" |
"loadServiceInfoStarted" | "loadServiceInfoComplete" | "loadServiceInfoFailed" |
"selectBundle" | "setOptions" |
"loadAvailableTimesStarted" | "loadAvailableTimesComplete" | "loadAvailableTimesFailed" |
"bookTemporaryTableStarted" | "bookTemporaryTableComplete" | "bookTemporaryTableFailed" |
"clearTemporaryTableStarted" | "clearTemporaryTableComplete" | "clearTemporaryTableFailed" |
"createBookingStarted" | "createBookingComplete" | "createBookingFailed" |
"cancelBookingStarted" | "cancelBookingComplete" | "cancelBookingFailed" |
"verifyVoucherStarted" | "verifyVoucherComplete" | "verifyVoucherFailed" |
"verifyDiscountCodeStarted" | "verifyDiscountCodeComplete" | "verifyDiscountCodeFailed" |
"clearDiscountCodes" |
"openSeatmap" | "closeSeatmap" | "selectSeat" |
"getCarriageLayoutStarted" | "getCarriageLayoutComplete" | "getCarriageLayoutFailed" |
"updateSeatsStarted" | "updateSeatsComplete" | "updateSeatsFailed" |
"finalizeBookingStarted" | "finalizeBookingComplete" | "finalizeBookingFailed" |
"abortPayment" |
"fakePaymentStarted" | "fakePaymentComplete" | "fakePaymentFailed" |
"checkPaymentStatusStarted" | "checkPaymentStatusComplete" | "checkPaymentStatusFailed";


export const showDialog = (search?: { origin?: IStop, destination?: IStop }) => ({ type: "showDialog", search });
export const setStep = (step: BookingDialogStep) => ({ type: "setStep", step });

export const selectRoute = (direction: Direction, route: IRoute) => ({ type: "selectRoute", direction, route });

export const loadServiceInfo = (direction: Direction, routeId: string, serviceIdentifiers: string[]) => {
	const payload = {
		serviceIdentifiers
	};

	const meta = { direction, routeId };

	return {
		type: "loadServiceInfo",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/orientation/searchservices`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "loadServiceInfoStarted" },
				{ meta, type: "loadServiceInfoComplete" },
				{ meta, type: "loadServiceInfoFailed" }
			]
		}
	};

};

export const loadAvailableTimes = (leg: IRouteLeg, persons: number, productCode: string, slotStartTime: string, slotEndTime: string, slotOffsetDays: number) => {


	const payload = {
		departureTime: leg.departureStation.departureTimestamp,
		arrivalTime: leg.arrivalStation.arrivalTimestamp,
		serviceName: leg.serviceName,
		slotStartTime,
		slotEndTime,
		slotOffsetDays,
		persons,
		productCode
	};

	const meta = {
		legId: leg.id,
		productCode
	}

	return {
		type: "loadAvailableTimes",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/tablebooking/availabletimes`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "loadAvailableTimesStarted" },
				{ meta, type: "loadAvailableTimesComplete" },
				{ meta, type: "loadAvailableTimesFailed" }
			]
		}
	};

};

export const bookTemporaryTable = (date: string, time: string, serviceName: string, persons: number, productCode: string, legId: string, slotName: string) => {

	const meta = {
		legId,
		productCode,
		date,
		time,
		persons,
		serviceName,
		slotName
	};

	const payload = {
		date,
		time,
		serviceName,
		persons
	}

	return {
		type: "bookTemporaryTable",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/tablebooking/booktemporary`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "bookTemporaryTableStarted" },
				{ meta, type: "bookTemporaryTableComplete" },
				{ meta, type: "bookTemporaryTableFailed" }
			]
		}
	};
};

export const clearTemporaryTable = (bookingId: number) => {

	const meta = { bookingId };


	return {
		type: "clearTemporaryTable",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/tablebooking/temporary/${bookingId}`,
			headers: getDefaultHeaders(),
			method: "DELETE",
			types: [
				{ meta, type: "clearTemporaryTableStarted" },
				{ meta, type: "clearTemporaryTableComplete" },
				{ meta, type: "clearTemporaryTableFailed" }
			]
		}
	};
};

export const selectBundle = (direction: Direction, bundle: IBundle) => ({ type: "selectBundle", direction, bundle });
export const setOptions = (direction: Direction, tariffCode: string, count: number, legId: string) => ({ type: "setOptions", direction, tariffCode, count, legId });

export const createBooking = (travelState: ITravelDetailsState, passengers: {id: string, type: string}[], interrailPasses?: string[]) => {

	let segments = travelState.outbound.route.legs.map(leg => legToSegment(leg, "outbound", travelState.outbound.bundle, travelState.outbound.options));

	if (travelState.inbound?.route)
		segments = segments.concat(travelState.inbound.route.legs.map(leg => legToSegment(leg, "inbound", travelState.inbound.bundle, travelState.inbound.options)));

	const payload = {
		segments,
		passengers,
		interrailPasses
	};

	return {
		type: "createBooking",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/booking`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ type: "createBookingStarted" },
				{ type: "createBookingComplete" },
				{ type: "createBookingFailed" }
			]
		}
	};
}

function legToSegment(leg: IRouteLeg, direction: Direction, bundle: IBundle, options?: { [legId: string]: { [tariffCode: string]: number } })
{
	//Items
	const bundleItems = bundle.requiredItems.filter(x => x.legIds.some(legId => legId === leg.id)) || [];
	const items = bundleItems.flatMap(item => item.passengerFares).map(p => ({ passengerId: p.passengerId, tariffCode: p.tariffCode }))

	//Options
	const selectedOptions = options && options[leg.id] ? options[leg.id] : {};
	const legOptions = bundle.options.filter(x => x.legIds.some(legId => legId === leg.id)) || [];
	const selectedLegOptions = legOptions.filter(x => selectedOptions[x.passengerFares[0].tariffCode] > 0)
		.map(opt => ({ opt, count: selectedOptions[opt.passengerFares[0].tariffCode] }));
	const optionItems = selectedLegOptions.flatMap(x => x.opt.passengerFares.slice(0, x.count)).map(p => ({ passengerId: p.passengerId, tariffCode: p.tariffCode }));

	return {
		origin: leg.departureStation.uicStationCode,
		destination: leg.arrivalStation.uicStationCode,
		direction: direction,
		departure: leg.departureStation.departureTimestamp,
		serviceName: leg.serviceName,
		serviceIdentifier: leg.serviceIdentifier,
		items,
		optionItems
	};
}


export const cancelBooking = (bookingNumber: string) => {
	const meta = { bookingNumber };

	return {
		type: "cancelBooking",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/booking/${bookingNumber}/cancel`,
			headers: getDefaultHeaders(),
			method: "POST",
			types: [
				{ meta, type: "cancelBookingStarted" },
				{ meta, type: "cancelBookingComplete" },
				{ meta, type: "cancelBookingFailed" }
			]
		}
	};
}

export const verifyVoucher = (bookingNumber: string, voucherCode: string, email?: string, travelDates?: string[]) => {
	const meta = { bookingNumber, voucherCode };

	const payload = {
		bookingNumber,
		voucherCode,
		email,
		travelDates
	}

	return {
		type: "verifyVoucher",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/vouchers/verify`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "verifyVoucherStarted" },
				{ meta, type: "verifyVoucherComplete" },
				{ meta, type: "verifyVoucherFailed" }
			]
		}
	};
}

export const verifyDiscountCode = (bookingNumber: string, voucherCode: string, email?: string, travelDates?: string[]) => {
	const meta = { bookingNumber, voucherCode };

	const payload = {
		bookingNumber,
		voucherCode,
		email,
		travelDates
	}

	return {
		type: "verifyDiscountCode",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/vouchers/discountcode/verify`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "verifyDiscountCodeStarted" },
				{ meta, type: "verifyDiscountCodeComplete" },
				{ meta, type: "verifyDiscountCodeFailed" }
			]
		}
	};
}

export const clearDiscountCodes = () => ({ type: "clearDiscountCodes" });

export const openSeatmap = (tariffSegment: IBookingTariffSegment) => ({ type: "openSeatmap", tariffSegment} );
export const closeSeatmap = () => ({ type: "closeSeatmap"} );

export const getCarriageLayout = (serviceName: string, from: string, to: string, travelDate: string) => {
	const meta = { serviceName, from, to, travelDate };

	return {
		type: "getCarriageLayout",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/meta/carriagelayouts/${serviceName}/${from}/${to}/${travelDate}`,
			headers: getDefaultHeaders(),
			method: "GET",
			types: [
				{ meta, type: "getCarriageLayoutStarted" },
				{ meta, type: "getCarriageLayoutComplete" },
				{ meta, type: "getCarriageLayoutFailed" }
			]
		}
	};
};

export const selectSeat = (itemId: string, seat: ISeat) => ({ type: "selectSeat", itemId, seat });

export const updateSeats = (bookingNumber: string, seats: { itemId: string, carriage: string, number: string }[]) => {
	const meta = { bookingNumber };
	const payload = { seats };

	return {
		type: "updateSeats",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/booking/${bookingNumber}/seats`,
			headers: getDefaultHeaders(),
			method: "PATCH",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "updateSeatsStarted" },
				{ meta, type: "updateSeatsComplete" },
				{ meta, type: "updateSeatsFailed" }
			]
		}
	};
};

export const finalizeBooking = (bookingNumber: string, payload: any) => {
	const meta = { bookingNumber, paymentMethod: payload.paymentMethod };

	return {
		type: "finalizeBooking",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/booking/${bookingNumber}/finalize`,
			headers: getDefaultHeaders(),
			method: "POST",
			body: JSON.stringify(payload),
			types: [
				{ meta, type: "finalizeBookingStarted" },
				{ meta, type: "finalizeBookingComplete" },
				{ meta, type: "finalizeBookingFailed" }
			]
		}
	};
}

export const abortPayment = () => ({ type: "abortPayment" });

export const fakePayment = (bookingNumber: string, reference: string) => {
	const meta = { bookingNumber };

	return {
		type: "fakePayment",
		[RSAA]: {
			endpoint: `${getSettings().apiUrl}/booking/${bookingNumber}/fakePayment?reference=${reference}`,
			headers: getDefaultHeaders(),
			method: "POST",
			types: [
				{ meta, type: "fakePaymentStarted" },
				{ meta, type: "fakePaymentComplete" },
				{ meta, type: "fakePaymentFailed" }
			]
		}
	};
};